// src/context/AuthContext.js
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { hasPlatformPermission, hasTenantPermission } from '../auth/permissions';
import {
  clearServerProviderCapabilities,
  setServerProviderCapabilities,
} from '../baking/executionProviders';
import {
  clearServerAiRiggingFlags,
  setServerAiRiggingFlags,
} from '../features/aiRigging';
import { API_BASE_URL } from '../config/apiBase';
import bakingApi from '../services/bakingApi';

const AuthContext = createContext();

const API_URL = API_BASE_URL;
const ACTIVE_TENANT_KEY = 'bakeonator:activeTenantId';
const ACTIVE_PARTNER_KEY = 'bakeonator:activePartnerId';

export { AuthContext };
export const useAuth = () => useContext(AuthContext);

function pickActiveMembership(memberships, preferredTenantId, sessionActiveTenant) {
  if (!memberships || memberships.length === 0) return null;

  if (preferredTenantId) {
    const stored = memberships.find(
      (m) => m.tenant_id === preferredTenantId || m.partner_id === preferredTenantId
    );
    if (stored) return stored;
  }

  const sessionId = sessionActiveTenant?.tenant_id || sessionActiveTenant?.partner_id;
  if (sessionId) {
    const fromSession = memberships.find(
      (m) => m.tenant_id === sessionId || m.partner_id === sessionId
    );
    if (fromSession) return fromSession;
  }

  return memberships[0];
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [activeTenant, setActiveTenantState] = useState(null);
  const [activeTenantId, setActiveTenantId] = useState(null);
  const [platformPermissions, setPlatformPermissions] = useState([]);
  const [platformRoles, setPlatformRoles] = useState([]);
  const [hasPlatformAccess, setHasPlatformAccess] = useState(false);
  const [aiRiggingFlags, setAiRiggingFlags] = useState(null);
  const [capabilitiesLoaded, setCapabilitiesLoaded] = useState(false);

  const applySession = useCallback((data) => {
    const authed = Boolean(data.isauthenticated);
    setIsAuthenticated(authed);
    if (!authed) {
      setUser(null);
      setMemberships([]);
      setActiveTenantState(null);
      setActiveTenantId(null);
      setPlatformPermissions([]);
      setPlatformRoles([]);
      setHasPlatformAccess(false);
      setAiRiggingFlags(null);
      setCapabilitiesLoaded(false);
      bakingApi.setTenantId(null);
      clearServerProviderCapabilities();
      clearServerAiRiggingFlags();
      return;
    }

    const nextMemberships = data.memberships || [];
    const storedTenantId =
      localStorage.getItem(ACTIVE_PARTNER_KEY) || localStorage.getItem(ACTIVE_TENANT_KEY);
    const nextActive = pickActiveMembership(
      nextMemberships,
      storedTenantId,
      data.active_partner || data.active_tenant
    );

    setUser({
      id: data.id,
      email: data.email,
      first_name: data.first_name,
      last_name: data.last_name,
      display_name: data.display_name,
    });
    setMemberships(nextMemberships);
    setActiveTenantState(nextActive);
    setActiveTenantId(nextActive ? nextActive.tenant_id : null);
    setPlatformPermissions(data.platform_permissions || []);
    setPlatformRoles(data.platform_roles || []);
    setHasPlatformAccess(Boolean(data.has_platform_access));

    if (nextActive) {
      localStorage.setItem(ACTIVE_TENANT_KEY, nextActive.tenant_id);
      localStorage.setItem(ACTIVE_PARTNER_KEY, nextActive.tenant_id);
      bakingApi.setTenantId(nextActive.tenant_id);
    } else {
      localStorage.removeItem(ACTIVE_TENANT_KEY);
      localStorage.removeItem(ACTIVE_PARTNER_KEY);
      bakingApi.setTenantId(null);
    }

    bakingApi
      .listExecutionProviders()
      .then((payload) => {
        setServerProviderCapabilities(payload);
        setServerAiRiggingFlags(payload);
        setAiRiggingFlags(payload?.ai_rigging || null);
        setCapabilitiesLoaded(true);
      })
      .catch(() => {
        clearServerProviderCapabilities();
        clearServerAiRiggingFlags();
        setAiRiggingFlags(null);
        setCapabilitiesLoaded(true);
      });
  }, []);

  const refreshSession = useCallback(async () => {
    setSessionLoading(true);
    try {
      const headers = {};
      const storedTenantId =
        localStorage.getItem(ACTIVE_PARTNER_KEY) || localStorage.getItem(ACTIVE_TENANT_KEY);
      if (storedTenantId) {
        headers['X-Tenant-Id'] = storedTenantId;
        headers['X-Partner-Id'] = storedTenantId;
      }
      const res = await fetch(`${API_URL}/session/`, {
        credentials: 'include',
        headers,
      });
      const data = await res.json();
      applySession(data);
      return data;
    } catch (err) {
      console.error('Error fetching session:', err);
      setIsAuthenticated(false);
      return null;
    } finally {
      setSessionLoading(false);
    }
  }, [applySession]);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const setActiveTenant = useCallback(
    (tenantId) => {
      if (!tenantId) {
        setActiveTenantState(null);
        setActiveTenantId(null);
        localStorage.removeItem(ACTIVE_TENANT_KEY);
        localStorage.removeItem(ACTIVE_PARTNER_KEY);
        bakingApi.setTenantId(null);
        return;
      }
      const membership = memberships.find(
        (m) => m.tenant_id === tenantId || m.partner_id === tenantId
      );
      if (!membership) return;
      setActiveTenantState(membership);
      setActiveTenantId(tenantId);
      localStorage.setItem(ACTIVE_TENANT_KEY, tenantId);
      localStorage.setItem(ACTIVE_PARTNER_KEY, tenantId);
      bakingApi.setTenantId(tenantId);
    },
    [memberships]
  );

  const hasTenantPermissionFn = useCallback(
    (permission) => hasTenantPermission(activeTenant?.permissions, permission),
    [activeTenant]
  );

  const hasPlatformPermissionFn = useCallback(
    (permission) => hasPlatformPermission(platformPermissions, permission),
    [platformPermissions]
  );

  const value = useMemo(
    () => ({
      isAuthenticated,
      setIsAuthenticated,
      sessionLoading,
      user,
      memberships,
      partners: memberships,
      activeTenant,
      activePartner: activeTenant,
      activeTenantId,
      activePartnerId: activeTenantId,
      setActiveTenant,
      setActivePartner: setActiveTenant,
      platformPermissions,
      platformRoles,
      hasPlatformAccess,
      refreshSession,
      hasTenantPermission: hasTenantPermissionFn,
      hasPartnerPermission: hasTenantPermissionFn,
      hasPlatformPermission: hasPlatformPermissionFn,
      aiRiggingFlags,
      capabilitiesLoaded,
    }),
    [
      isAuthenticated,
      sessionLoading,
      user,
      memberships,
      activeTenant,
      activeTenantId,
      setActiveTenant,
      platformPermissions,
      platformRoles,
      hasPlatformAccess,
      refreshSession,
      hasTenantPermissionFn,
      hasPlatformPermissionFn,
      aiRiggingFlags,
      capabilitiesLoaded,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
